import { apiRequest, apiUpload } from '../api';

/** Параметр набора настроек: имя + возможные значения (для рандомизатора). */
export interface ModSettingParam {
  id: number;
  name: string;
  values: string[];
  /** Рандомизируется ли параметр по умолчанию. */
  randomize: boolean;
  /** Значение по умолчанию (если задано). */
  default_value: string | null;
  /** Имя параметра-переключателя: параметр активен только при его значениях. */
  depends_on: string | null;
  /** Значения родительского параметра, при которых параметр активен. */
  depends_values: string[];
  /** true — параметр исключён из рандомизации целиком (настройка модератора). */
  disabled: boolean;
  /** Значения, которые не должны выпадать при рандомизации. */
  excluded_values: string[];
}

/** Единственный набор настроек мода (название набора не используется). */
export interface ModSetting {
  params: ModSettingParam[];
}

/** Пресет настроек мода: имя + значения параметров (имя параметра → значение). */
export interface ModSettingPreset {
  id: number;
  name: string;
  values: Record<string, string>;
}

/** Мод в публичном разделе «Моды» вместе с голосами и настройками. */
export interface ModListItem {
  id: number;
  name: string;
  author: string | null;
  description: string | null;
  /** Настройки мода; null — если их нет. */
  settings: ModSetting | null;
  /** Пресеты настроек мода. */
  presets: ModSettingPreset[];
  likes: number;
  dislikes: number;
  /** Сколько игр сыграно с этим модом (популярность). */
  games_count: number;
  /** Мой голос: 1 — лайк, -1 — дизлайк, 0 — нет. */
  my_vote: number;
}

export interface ModVoteResult {
  likes: number;
  dislikes: number;
  my_vote: number;
}

/** Настройки для сохранения (без id — сервер пересоздаёт набор). */
export interface ModSettingsPayload {
  params: Array<{
    name: string;
    values: string[];
    randomize: boolean;
    default_value: string | null;
    /** Ветвление: параметр активен только при этих значениях родителя. */
    depends_on: string | null;
    depends_values: string[];
    /** Параметр исключён из рандомизации целиком. */
    disabled: boolean;
    /** Значения, исключённые из розыгрыша. */
    excluded_values: string[];
  }>;
  /** Пресеты настроек (значения параметров). */
  presets: Array<{
    name: string;
    values: Record<string, string>;
  }>;
}

export async function listMods(token: string | null): Promise<ModListItem[]> {
  const res = await apiRequest<{ mods: ModListItem[] }>('/api/mods', { token });
  return res.mods;
}

export async function voteMod(
  id: number,
  value: 1 | -1 | 0,
  token: string | null
): Promise<ModVoteResult> {
  return apiRequest<ModVoteResult>(`/api/mods/${id}/vote`, {
    method: 'POST',
    token,
    body: { value },
  });
}

/** Опции лобби из файла мода (сервер скачивает его из депо Blizzard по реплею). */
export interface ModFileSettings {
  name: string | null;
  isExtensionMod: boolean;
  params: Array<{
    name: string;
    values: string[];
    default_value: string | null;
    depends_on: string | null;
    depends_values: string[];
  }>;
}

/** Настройки лобби модов, на которых сыгран реплей (модератор / ghost / админ). */
export async function modSettingsFromReplay(
  file: File,
  token: string | null
): Promise<{ candidates: ModFileSettings[]; downloaded: number; total: number }> {
  return apiUpload('/api/mods/settings-from-replay', file, file.name, token);
}

/** Заменяет набор настроек мода (модератор / ghost / админ). */
export async function saveModSettings(
  id: number,
  settings: ModSettingsPayload,
  token: string | null
): Promise<{ settings: ModSetting | null; presets: ModSettingPreset[] }> {
  return apiRequest<{ settings: ModSetting | null; presets: ModSettingPreset[] }>(
    `/api/mods/${id}/settings`,
    {
      method: 'PATCH',
      token,
      // Сервер ждёт параметры в settings, а пресеты — отдельным полем верхнего уровня.
      body: { settings: { params: settings.params }, presets: settings.presets },
    }
  );
}
